import * as RadixDialog from '@radix-ui/react-dialog';
import { useRef, type ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * A panel that slides in from the starting edge — the left in English, the
 * right in Arabic — over a dimmed page.
 *
 * Built on Radix Dialog for the behaviour a hand-rolled drawer gets wrong:
 * focus moves in when it opens and back to whatever opened it when it closes,
 * Tab cannot wander off into the page behind, Escape closes it, and the page
 * behind is hidden from assistive technology. While closed it is not in the
 * document at all, so its links are not in the tab order — the navigation
 * drawer used to be merely translated off-screen, and keyboard users tabbed
 * through a dozen invisible links before reaching the page.
 */
export function SideSheet({
  open,
  onOpenChange,
  title,
  children,
  className,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Its accessible name. Not shown; the content carries its own heading. */
  title: string;
  children: ReactNode;
  className?: string;
}) {
  /**
   * Whatever had focus when the sheet opened — usually the menu button.
   *
   * Radix hands focus back to a `Dialog.Trigger` on close, and the button that
   * opens this sheet lives in another component, so without this focus fell
   * to the top of the document and a keyboard user had to start again.
   */
  const returnFocusTo = useRef<HTMLElement | null>(null);

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="animate-fade fixed inset-0 z-40 bg-overlay backdrop-blur-[1px]" />
        <RadixDialog.Content
          aria-describedby={undefined}
          onOpenAutoFocus={() => {
            // Called before focus moves in, so this is still the opener.
            returnFocusTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            returnFocusTo.current?.focus();
          }}
          className={cn(
            'animate-sheet-start fixed inset-y-0 start-0 z-50 flex w-72 max-w-[85vw] flex-col',
            'border-e border-line bg-surface shadow-float outline-none',
            className,
          )}
        >
          <RadixDialog.Title className="sr-only-focusable">{title}</RadixDialog.Title>
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/** Closes the sheet it sits in — for a close button in the sheet's own header. */
export const SideSheetClose = RadixDialog.Close;
